<template>
  <div class="desktop-config-page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>桌面版 · Wenling EATing ERP（Mac / Windows）</span>
        <el-button type="primary" size="small" :loading="saving" icon="el-icon-check" @click="publish">
          保存并发布
        </el-button>
      </div>

      <p class="hint">
        上传安装包并填写版本号后点「保存并发布」。桌面端启动会自动检查更新；用户须勾选同意《用户协议》与《隐私政策》后才会下载，下载完成后自动安装。
        版本号须高于用户当前版本（如当前 1.0.3，请发 1.0.4）。
        若上传报 413，请在 Nginx 增加 <code>client_max_body_size 512m;</code> 后重载再传。
      </p>

      <el-form label-width="120px" size="small" class="form">
        <el-form-item label="发布版本号" required>
          <el-input v-model="form.version" placeholder="例如 1.0.2" style="width: 200px" clearable />
        </el-form-item>
        <el-form-item label="强制更新">
          <el-switch v-model="form.force" />
          <span class="hint inline">开启后只能点「立即更新」，无法稍后</span>
        </el-form-item>
        <el-form-item label="更新说明">
          <el-input
            v-model="form.notes"
            type="textarea"
            :rows="4"
            placeholder="本次更新内容，将显示在桌面端升级弹窗中"
            maxlength="2000"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="最近发布">
          <span>{{ form.published_at || '尚未发布' }}</span>
        </el-form-item>
      </el-form>

      <el-divider content-position="left">Mac 安装包（.dmg）</el-divider>
      <div class="pkg-row">
        <div class="pkg-info">
          <div v-if="form.mac && form.mac.url">
            <div><strong>{{ form.mac.filename || '已上传' }}</strong></div>
            <div class="meta">{{ formatSize(form.mac.size) }} · {{ form.mac.updated_at || '-' }}</div>
            <div class="meta url">{{ form.mac.url }}</div>
          </div>
          <div v-else class="empty">尚未上传 Mac 安装包</div>
        </div>
        <div class="pkg-ops">
          <el-upload :show-file-list="false" :http-request="(req) => uploadPkg('mac', req)" accept=".dmg,.zip">
            <el-button size="small" type="primary" :loading="uploadingMac" icon="el-icon-upload2">上传 Mac 包</el-button>
          </el-upload>
          <el-button
            size="small"
            :disabled="!(form.mac && form.mac.url)"
            @click="downloadPkg('mac')"
          >下载</el-button>
        </div>
      </div>

      <el-divider content-position="left">Windows 安装包（.exe）</el-divider>
      <div class="pkg-row">
        <div class="pkg-info">
          <div v-if="form.win && form.win.url">
            <div><strong>{{ form.win.filename || '已上传' }}</strong></div>
            <div class="meta">{{ formatSize(form.win.size) }} · {{ form.win.updated_at || '-' }}</div>
            <div class="meta url">{{ form.win.url }}</div>
          </div>
          <div v-else class="empty">尚未上传 Windows 安装包</div>
        </div>
        <div class="pkg-ops">
          <el-upload :show-file-list="false" :http-request="(req) => uploadPkg('win', req)" accept=".exe,.msi,.zip">
            <el-button size="small" type="primary" :loading="uploadingWin" icon="el-icon-upload2">上传 Windows 包</el-button>
          </el-upload>
          <el-button
            size="small"
            :disabled="!(form.win && form.win.url)"
            @click="downloadPkg('win')"
          >下载</el-button>
        </div>
      </div>

      <el-divider content-position="left">公开接口（给桌面端用）</el-divider>
      <div class="api-box">
        <div>检查更新：<code>{{ origin }}/api/desktop/update-check?platform=darwin&amp;version=1.0.0</code></div>
        <div>Mac 下载：<code>{{ origin }}/api/desktop/download/mac</code></div>
        <div>Windows 下载：<code>{{ origin }}/api/desktop/download/win</code></div>
      </div>
    </el-card>
  </div>
</template>

<script>
function emptyForm() {
  return {
    version: '',
    force: false,
    notes: '',
    published_at: '',
    mac: { filename: '', url: '', size: 0, updated_at: '' },
    win: { filename: '', url: '', size: 0, updated_at: '' }
  }
}

export default {
  name: 'DesktopConfig',
  data() {
    return {
      form: emptyForm(),
      saving: false,
      uploadingMac: false,
      uploadingWin: false
    }
  },
  computed: {
    origin() {
      if (typeof window === 'undefined') return ''
      return window.location.origin
    }
  },
  created() {
    this.load()
  },
  methods: {
    formatSize(n) {
      const s = Number(n) || 0
      if (s < 1024) return s + ' B'
      if (s < 1024 * 1024) return (s / 1024).toFixed(1) + ' KB'
      return (s / (1024 * 1024)).toFixed(1) + ' MB'
    },
    apply(data) {
      const d = data && typeof data === 'object' ? data : {}
      this.form = {
        version: d.version || '',
        force: !!d.force,
        notes: d.notes || '',
        published_at: d.published_at || '',
        mac: Object.assign({ filename: '', url: '', size: 0, updated_at: '' }, d.mac || {}),
        win: Object.assign({ filename: '', url: '', size: 0, updated_at: '' }, d.win || {})
      }
    },
    async load() {
      try {
        const resp = await this.$api.get('/admin/desktop-config')
        if (resp && resp.code === 200) this.apply(resp.data)
      } catch (e) {
        this.$message.error('加载桌面版配置失败')
      }
    },
    async publish() {
      const ver = String(this.form.version || '').trim()
      if (!ver) {
        this.$message.warning('请填写发布版本号')
        return
      }
      if (!(this.form.mac && this.form.mac.url) && !(this.form.win && this.form.win.url)) {
        this.$message.warning('请至少上传一个平台的安装包')
        return
      }
      this.saving = true
      try {
        const payload = {
          version: ver,
          force: !!this.form.force,
          notes: this.form.notes || '',
          published_at: new Date().toISOString(),
          mac: this.form.mac,
          win: this.form.win
        }
        const resp = await this.$api.put('/admin/desktop-config', payload)
        if (resp && resp.code === 200) {
          this.apply(resp.data)
          this.$message.success((resp && resp.message) || '已发布')
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async uploadPkg(platform, req) {
      const file = req && req.file
      if (!file) return
      if (platform === 'mac') this.uploadingMac = true
      else this.uploadingWin = true
      try {
        const fd = new FormData()
        fd.append('file', file)
        const resp = await this.$api.post(
          `/admin/desktop-config/upload?platform=${encodeURIComponent(platform)}`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }
        )
        if (resp && resp.code === 200 && resp.data) {
          if (resp.data.config) this.apply(resp.data.config)
          this.$message.success('上传成功，请确认版本号后点「保存并发布」')
        } else {
          this.$message.error((resp && resp.message) || '上传失败')
        }
      } catch (e) {
        const status = e && e.response && e.response.status
        if (status === 413) {
          this.$message.error(
            '上传失败：文件太大（413）。请在服务器 Nginx 配置中增加 client_max_body_size 512m; 后重载 Nginx，再重新上传。'
          )
        } else {
          this.$message.error((e && e.message) || '上传失败')
        }
      } finally {
        if (platform === 'mac') this.uploadingMac = false
        else this.uploadingWin = false
      }
    },
    downloadPkg(platform) {
      const pkg = platform === 'mac' ? this.form.mac : this.form.win
      if (!pkg || !pkg.url) return
      const url = /^https?:\/\//i.test(pkg.url) ? pkg.url : `${this.origin}${pkg.url}`
      window.open(url, '_blank')
    }
  }
}
</script>

<style scoped>
.desktop-config-page {
  max-width: 920px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.hint {
  color: #909399;
  font-size: 13px;
  line-height: 1.6;
  margin: 0 0 16px;
}
.hint.inline {
  margin-left: 12px;
}
.pkg-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}
.pkg-info {
  flex: 1;
  min-width: 0;
}
.pkg-ops {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}
.meta {
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
}
.meta.url {
  word-break: break-all;
}
.empty {
  color: #c0c4cc;
}
.api-box {
  background: #f5f7fa;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.8;
  color: #606266;
}
.api-box code {
  font-size: 12px;
  color: #303133;
}
</style>
